import Axios, {AxiosResponse} from "axios";
import VulSourceItem, {AiExtractionPriceBreakdown, ReasoningSummaryNode, TokenUsage, VulSourceItemAiExtractorConfig} from "../domain/VulSourceItem";
import QueryOptions from "../sal-ui/QueryOptions";
import PagedResult from "./PagedResult";
import VulSourceItemChangeLog from "../domain/VulSourceItemChangeLog";
import VulReport from "../domain/VulReport";
import Ticket from "../domain/Ticket";

export interface PromptPreviewEstimate {
    characters: number;
    tokens: number;
}

export interface PromptPreviewMessage {
    role: string;
    label: string;
    content: string;
    estimate?: PromptPreviewEstimate;
}

export interface PromptPreviewResponse {
    messages: PromptPreviewMessage[];
    estimate?: PromptPreviewEstimate;
}

export interface AiExtractDryRunResponse {
    aiAttributes?: unknown;
    dryRunId?: string;
    aiProviderCommunicationRunId?: string;
    tokenUsage?: TokenUsage;
    executionTimeMs?: number;
    reasoningSummary?: ReasoningSummaryNode[];
    toolTelemetry?: unknown;
    aiExtractorConfig?: VulSourceItemAiExtractorConfig;
    priceBreakdown?: AiExtractionPriceBreakdown;
}

export type AiProviderCommunicationLoggingMode = "DEFAULT" | "ENABLED" | "DISABLED";

export interface ItemUpdateProfileChangeLogMatch {
    profileId: string;
    profileName: string;
    owner: string;
    organizationName?: string;
    matched: boolean;
}

export interface LazyJsonData {
    raw?: string;
    parsed?: any;
}

export interface ChangeLogData {
    jsonDataRaw?: string;
    jsonData?: any;
    attributesRaw?: string;
    attributes?: any;
}

function parseLazyJsonField(value?: string | null): LazyJsonData {
    const raw = value ?? undefined;

    return {
        raw,
        parsed: raw ? JSON.parse(raw) : null
    };
}

class VulSourceItemService {

    readonly basePath = "/vul-source-items";

    public add(vulSourceItem: VulSourceItem): Promise<string> {
        return Axios.post(`${this.basePath}`, vulSourceItem).then(value => value.data.id)
    }

    public update(vulSourceItemId: string, vulSourceItem: VulSourceItem): Promise<void> {
        return Axios.put(`${this.basePath}/${vulSourceItemId}`, vulSourceItem);
    }

    public get(id: string): Promise<VulSourceItem> {
        return Axios.get(`${this.basePath}/${id}`).then(value => VulSourceItem.fromPOJO(value.data));
    }

    public getJsonData(id: string): Promise<LazyJsonData> {
        return Axios.get(`${this.basePath}/${id}/json-data`)
            .then(value => parseLazyJsonField(value.data.jsonData));
    }

    public getChangeLogData(id: string, changeLogId: string): Promise<ChangeLogData> {
        return Axios.get(`${this.basePath}/${id}/change-logs/${changeLogId}/data`)
            .then(value => {
                const jsonData = parseLazyJsonField(value.data.jsonData);
                const attributes = parseLazyJsonField(value.data.attributes);

                return {
                    jsonDataRaw: jsonData.raw,
                    jsonData: jsonData.parsed,
                    attributesRaw: attributes.raw,
                    attributes: attributes.parsed
                };
            });
    }

    public getRelatedItems(id: string, relatedItemsMaxDepth: number): Promise<VulSourceItem[]> {
        return Axios.get(`${this.basePath}/${id}/related-items?maxDepth=${relatedItemsMaxDepth}`)
            .then(value => {
                const objectList: VulSourceItem[] = [];

                value.data.forEach((objectJson: object) => {
                    objectList.push(VulSourceItem.fromPOJO(objectJson));
                });

                return objectList;
            })
    }

    public getRelatedTickets(id: string, relatedItemsMaxDepth: number): Promise<Ticket[]> {
        return Axios.get(`${this.basePath}/${id}/related-tickets?maxDepth=${relatedItemsMaxDepth}`)
            .then(value => {
                const objectList: Ticket[] = [];

                value.data.forEach((objectJson: object) => {
                    objectList.push(Ticket.fromPOJO(objectJson));
                });

                return objectList;
            })
    }

    public delete(vulSourceItem: VulSourceItem): Promise<void> {
        return Axios.delete(`${this.basePath}/${vulSourceItem.id}`);
    }

    public getList(queryOptions: QueryOptions): Promise<PagedResult<VulSourceItem>> {
        return Axios.get(`${this.basePath}?${queryOptions.toQueryParams()}`)
            .then(value => {
                const objectList: VulSourceItem[] = [];

                value.data.data.forEach((objectJson: object) => {
                    objectList.push(VulSourceItem.fromPOJO(objectJson));
                });

                return new PagedResult(value.data.total, objectList);
            })
    }

    public getListForReport(
        queryOptions: QueryOptions,
        report: VulReport,
        profilePreviewId?: string,
        itemTypes?: string[],
        aiSummaryGroup?: string,
        aiSummaryGrouping?: boolean
    ): Promise<PagedResult<VulSourceItem> & { newItems: number, modifiedItems: number }> {
        const extraParams = new URLSearchParams();

        if (profilePreviewId) {
            extraParams.append("profilePreviewId", profilePreviewId);
        }

        if (itemTypes) {
            itemTypes.forEach(itemType => extraParams.append("itemType", itemType));
        }

        if (aiSummaryGroup) {
            extraParams.append("aiSummaryGroup", aiSummaryGroup);
        }

        if (aiSummaryGrouping) {
            extraParams.append("aiSummaryGrouping", "true");
        }

        const extraQuery = extraParams.size > 0 ? `&${extraParams.toString()}` : "";

        return Axios.get(`/vul-reports/${report.id}/items?${queryOptions.toQueryParams()}${extraQuery}`)
            .then(value => {
                const objectList: VulSourceItem[] = [];

                value.data.data.forEach((objectJson: object) => {
                    objectList.push(VulSourceItem.fromPOJO(objectJson));
                });

                return {
                    total: value.data.total,
                    newItems: value.data.newItems,
                    modifiedItems: value.data.modifiedItems,
                    data: objectList
                };
            })
    }

    public extractAndSaveAttributes(queryOptions: QueryOptions, skipAuditLogs: boolean, skipItemChangeLogs: boolean, skipUpdateFindings: boolean, skipAiExtraction: boolean): Promise<AxiosResponse> {
        return Axios.post(
            `${this.basePath}/extract-and-save?skipAuditLogs=${skipAuditLogs}` +
            `&skipItemChangeLogs=${skipItemChangeLogs}` +
            `&skipUpdateFindings=${skipUpdateFindings}` +
            `&skipAiExtraction=${skipAiExtraction}` +
            `&${queryOptions.toQueryParams()}`
        );
    }

    public aiExtractItems(queryOptions: QueryOptions, aiExtractorIds: string[], overwriteCurrentResult: boolean): Promise<AxiosResponse> {
        return Axios.post(`${this.basePath}/ai-extract?${queryOptions.toQueryParams()}`, {aiExtractorIds, overwriteCurrentResult});
    }

    public aiExtractItemsPreview(
        queryOptions: QueryOptions,
        aiExtractorIds: string[],
        overwriteCurrentResult: boolean,
        signal?: AbortSignal
    ): Promise<{ dispatchCount: number }> {
        return Axios.post(
            `${this.basePath}/ai-extract/preview?${queryOptions.toQueryParams()}`,
            {aiExtractorIds, overwriteCurrentResult},
            {signal}
        ).then(response => response.data);
    }

    public createTicket(vulSourceItem: VulSourceItem): Promise<string> {
        return Axios.post(`${this.basePath}/${vulSourceItem.id}/ticket`).then(value => value.data.id);
    }

    public extract(vulSourceItem: VulSourceItem): Promise<void> {
        return Axios.post(`${this.basePath}/${vulSourceItem.id}/extract`);
    }

    public extractDry(vulSourceItem: VulSourceItem): Promise<void> {
        return Axios.post(`${this.basePath}/${vulSourceItem.id}/extract/dry-run`);
    }

    public evaluate(vulSourceItem: VulSourceItem): Promise<void> {
        return Axios.post(`${this.basePath}/${vulSourceItem.id}/evaluate`);
    }

    public evaluateDry(vulSourceItem: VulSourceItem): Promise<void> {
        return Axios.post(`${this.basePath}/${vulSourceItem.id}/evaluate/dry-run`);
    }

    public aiExtract(vulSourceItem: VulSourceItem, aiExtractorId: string, dryRunId?: string): Promise<void> {
        const aiExtractorIdEncoded = encodeURIComponent(aiExtractorId);
        const dryRunQueryParam = dryRunId ? `&dryRunId=${encodeURIComponent(dryRunId)}` : "";

        return Axios.post(`${this.basePath}/${vulSourceItem.id}/ai-extract?aiExtractorId=${aiExtractorIdEncoded}${dryRunQueryParam}`);
    }

    public getPromptPreview(vulSourceItem: VulSourceItem, aiExtractorId: string): Promise<PromptPreviewResponse> {
        const aiExtractorIdEncoded = encodeURIComponent(aiExtractorId);

        return Axios.get(`${this.basePath}/${vulSourceItem.id}/ai-extract/prompt-preview?aiExtractorId=${aiExtractorIdEncoded}`)
            .then(response => response.data);
    }

    public aiExtractDry(vulSourceItem: VulSourceItem,
                        aiExtractorId: string,
                        providerCommunicationLoggingMode: AiProviderCommunicationLoggingMode = "DEFAULT"): Promise<AiExtractDryRunResponse> {
        const aiExtractorIdEncoded = encodeURIComponent(aiExtractorId);
        const loggingModeQueryParam = `&aiProviderCommunicationLoggingMode=${encodeURIComponent(providerCommunicationLoggingMode)}`;

        return Axios.post(`${this.basePath}/${vulSourceItem.id}/ai-extract/dry-run?aiExtractorId=${aiExtractorIdEncoded}${loggingModeQueryParam}`)
            .then(response => response.data);
    }

    public deleteAiExtractionResult(vulSourceItem: VulSourceItem, aiExtractionResultId: string): Promise<void> {
        return Axios.delete(`${this.basePath}/${vulSourceItem.id}/ai-extraction-results/${aiExtractionResultId}`);
    }

    public getChangeLogList(vulSourceItem: VulSourceItem): Promise<VulSourceItemChangeLog[]> {
        return Axios.get(`${this.basePath}/${vulSourceItem.id}/change-logs`)
            .then(value => {
                const objectList: VulSourceItemChangeLog[] = [];

                value.data.forEach((objectJson: object) => {
                    objectList.push(VulSourceItemChangeLog.fromPOJO(objectJson));
                });

                return objectList;
            })
    }

    public getItemUpdateProfileChangeLogMatches(
        vulSourceItem: VulSourceItem,
        changeLog: VulSourceItemChangeLog
    ): Promise<ItemUpdateProfileChangeLogMatch[]> {
        return Axios.get(`${this.basePath}/${vulSourceItem.id}/change-logs/${changeLog.id}/item-update-profile-matches`)
            .then(value => value.data);
    }

    public thumbUp(vulSourceItem: VulSourceItem) {
        return Axios.post(`${this.basePath}/${vulSourceItem.id}/thumb-up`);
    }

    public thumbDown(vulSourceItem: VulSourceItem) {
        return Axios.post(`${this.basePath}/${vulSourceItem.id}/thumb-down`);
    }
}

export default VulSourceItemService;
